import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { MapPin, Crosshair, Search, Eye, EyeOff, BadgeCheck, ShieldAlert, Save, Filter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const ATTRIB = '&copy; OpenStreetMap &copy; CARTO';

const personIcon = (photo, gold) => L.divIcon({
  className: "",
  html: `<div style="width:46px;height:46px;border-radius:50%;border:2px solid ${gold ? "#D4AF37" : "#888"};overflow:hidden;box-shadow:0 0 10px rgba(212,175,55,.55);background:#1A1A1A;display:flex;align-items:center;justify-content:center">
    ${photo ? `<img src="${photo}" style="width:100%;height:100%;object-fit:cover"/>` : `<span style="color:#D4AF37;font-size:20px">♛</span>`}
  </div>`,
  iconSize: [46, 46], iconAnchor: [23, 23], popupAnchor: [0, -24],
});

const meIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#D4AF37;border:3px solid #fff;box-shadow:0 0 0 6px rgba(212,175,55,.3)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function Recenter({ pos, zoom }) {
  const map = useMap();
  useEffect(() => { if (pos) map.flyTo([pos.lat, pos.lng], zoom || map.getZoom(), { duration: 0.8 }); }, [pos]); // eslint-disable-line
  return null;
}

function ClickToSet({ enabled, onSet }) {
  useMapEvents({ click(e) { if (enabled) onSet({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}

export default function MapPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [myPos, setMyPos] = useState(user?.location_lat != null ? { lat: user.location_lat, lng: user.location_lng } : null);
  const [sharePublic, setSharePublic] = useState(!!user?.location_public);
  const [precision, setPrecision] = useState(user?.location_precision || "approx");
  const [label, setLabel] = useState(user?.location_label || "");
  const [editing, setEditing] = useState(!user?.location_public);
  const [query, setQuery] = useState("");
  const [center, setCenter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [maxDist, setMaxDist] = useState("any");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [genderFilter, setGenderFilter] = useState("any");

  const load = () => api.get("/map/people").then((r) => setPeople(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const useMyGeo = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { const pos = { lat: p.coords.latitude, lng: p.coords.longitude }; setMyPos(pos); setCenter({ ...pos, _t: Date.now() }); toast.success("Location detected"); },
      () => toast.error("Couldn't get your location — check permissions"), { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (!data.length) return toast.error("Place not found");
      const pos = { lat: +data[0].lat, lng: +data[0].lon };
      setMyPos(pos); setCenter({ ...pos, _t: Date.now() });
      setLabel(data[0].display_name.split(",").slice(0, 2).join(",").trim());
    } catch { toast.error("Search failed"); }
  };

  const save = async () => {
    if (!myPos) return toast.error("Set your location first (detect, search, or tap the map)");
    if (sharePublic && precision === "exact" &&
        !window.confirm("⚠️ Your EXACT location will be publicly visible to all members. Anyone can see precisely where you are. Use 'Approximate' if you're unsure. Continue?")) {
      return;
    }
    setSaving(true);
    try {
      await api.put("/location", { lat: myPos.lat, lng: myPos.lng, public: sharePublic, precision, label });
      await refreshUser();
      load();
      setEditing(false);
      toast.success(sharePublic ? "You're on the map ✨" : "Location saved (hidden)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const goPrivate = async () => {
    try { await api.delete("/location"); await refreshUser(); setSharePublic(false); load(); toast.success("You're now hidden from the map"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const initialCenter = myPos || { lat: 20, lng: 0 };
  const initialZoom = myPos ? 12 : 2;
  const genders = useMemo(() => [...new Set(people.filter((p) => !p.is_me && p.gender).map((p) => p.gender))], [people]);
  const passesFilter = (p) => {
    if (verifiedOnly && !p.verified) return false;
    if (genderFilter !== "any" && p.gender !== genderFilter) return false;
    if (maxDist !== "any" && p.distance_km != null && p.distance_km > +maxDist) return false;
    return true;
  };
  const visiblePeople = useMemo(() => people.filter((p) => p.is_me || passesFilter(p)), [people, verifiedOnly, genderFilter, maxDist]);
  const nearby = useMemo(() => visiblePeople.filter((p) => !p.is_me && p.distance_km != null).sort((a, b) => a.distance_km - b.distance_km).slice(0, 8), [visiblePeople]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="overline mb-1">Meetups</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3"><MapPin className="text-[#D4AF37]" /> Discover People Near You</h2>
      <p className="text-[#A0A0A0] text-sm mb-6 max-w-2xl">Opt in to appear on the map and find members nearby for meetups. You're hidden until you turn sharing on — and you choose exactly how precise your pin is.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-[#2A2A2A]" style={{ height: 520 }}>
          <MapContainer center={[initialCenter.lat, initialCenter.lng]} zoom={initialZoom} style={{ height: "100%", width: "100%", background: "#0A0A0A" }} scrollWheelZoom>
            <TileLayer url={DARK_TILES} attribution={ATTRIB} />
            <Recenter pos={center} zoom={13} />
            <ClickToSet enabled={editing} onSet={(pos) => setMyPos(pos)} />
            {myPos && <Marker position={[myPos.lat, myPos.lng]} icon={meIcon}><Popup>Your location {sharePublic ? "(shared)" : "(hidden)"}</Popup></Marker>}
            {visiblePeople.filter((p) => !p.is_me).map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={personIcon(p.photo, p.is_creator)}>
                <Popup>
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                      {p.name} {p.verified && "✔"}
                    </div>
                    {p.label && <div style={{ fontSize: 11, color: "#666" }}>{p.label}</div>}
                    {p.distance_km != null && <div style={{ fontSize: 11, color: "#666" }}>~{p.distance_km} km away · {p.precision === "approx" ? "approx." : "exact"}</div>}
                    <button onClick={() => navigate(`/creator/${p.id}`)} style={{ marginTop: 6, background: "#D4AF37", color: "#000", border: "none", borderRadius: 999, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>View profile</button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <div className="pe-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading text-xl">Your location</div>
              {user?.location_public
                ? <span className="text-xs text-[#22c55e] flex items-center gap-1"><Eye size={13} /> Public</span>
                : <span className="text-xs text-[#A0A0A0] flex items-center gap-1"><EyeOff size={13} /> Hidden</span>}
            </div>

            {editing ? (
              <div className="space-y-3">
                <button data-testid="use-my-location" onClick={useMyGeo} className="btn-outline-gold w-full rounded-full py-2 text-sm flex items-center justify-center gap-2"><Crosshair size={15} /> Use my current location</button>
                <form onSubmit={search} className="flex gap-2">
                  <input data-testid="loc-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search a city / area" className="pe-input flex-1 rounded-lg px-3 py-2 text-sm" />
                  <button type="submit" className="btn-outline-gold px-3 rounded-lg"><Search size={15} /></button>
                </form>
                <div className="text-[11px] text-[#6b6b6b]">…or tap anywhere on the map to drop your pin.</div>

                <div>
                  <div className="text-xs text-[#A0A0A0] mb-1">Pin precision</div>
                  <div className="flex gap-2">
                    {[["approx", "Approximate (safer)"], ["exact", "Exact"]].map(([v, lbl]) => (
                      <button key={v} data-testid={`precision-${v}`} onClick={() => setPrecision(v)}
                        className={`flex-1 rounded-full py-2 text-xs transition-colors ${precision === v ? "gold-gradient text-black font-medium" : "border border-[#2A2A2A] text-[#A0A0A0]"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input data-testid="share-public" type="checkbox" checked={sharePublic} onChange={(e) => setSharePublic(e.target.checked)} />
                  Show me on the map (public)
                </label>

                {sharePublic && precision === "exact" && (
                  <div data-testid="exact-public-warning" className="rounded-lg p-2.5 border border-[#E63946]/50 bg-[#E63946]/10 text-[11px] text-[#ff9aa2] flex gap-2">
                    <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
                    <span>Your <strong>exact location</strong> will be publicly visible to every member. Switch to Approximate to blur your pin (~1.1 km).</span>
                  </div>
                )}

                <button data-testid="save-location" onClick={save} disabled={saving} className="btn-gold w-full rounded-full py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60"><Save size={15} /> {saving ? "Saving…" : "Save"}</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-[#A0A0A0]">{label || "Location set"} · {precision === "approx" ? "approximate pin" : "exact pin"}</div>
                <button data-testid="edit-location" onClick={() => setEditing(true)} className="btn-outline-gold w-full rounded-full py-2 text-sm">Edit location</button>
                {user?.location_public && <button data-testid="go-private" onClick={goPrivate} className="w-full rounded-full py-2 text-sm border border-[#E63946]/50 text-[#E63946] hover:bg-[#E63946]/10 flex items-center justify-center gap-2"><EyeOff size={15} /> Hide me</button>}
              </div>
            )}
          </div>

          {/* Safety */}
          <div className="rounded-2xl p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-xs text-[#D4AF37] flex gap-2">
            <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
            <span>Stay safe: meet in public places, tell a friend your plans, and never share financial info. Use "Approximate" unless you fully trust who can see you.</span>
          </div>

          {/* Filters */}
          <div className="pe-card rounded-2xl p-4 space-y-3">
            <div className="text-sm text-white font-medium flex items-center gap-2"><Filter size={14} className="text-[#D4AF37]" /> Filter the map</div>
            <div>
              <div className="text-[11px] text-[#A0A0A0] mb-1">Max distance</div>
              <select data-testid="filter-distance" value={maxDist} onChange={(e) => setMaxDist(e.target.value)} className="pe-input w-full rounded-lg px-3 py-2 text-sm">
                <option value="any">Any distance</option>
                <option value="10">Within 10 km</option>
                <option value="50">Within 50 km</option>
                <option value="100">Within 100 km</option>
                <option value="500">Within 500 km</option>
              </select>
            </div>
            <div>
              <div className="text-[11px] text-[#A0A0A0] mb-1">Gender</div>
              <select data-testid="filter-gender" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} className="pe-input w-full rounded-lg px-3 py-2 text-sm">
                <option value="any">Everyone</option>
                {genders.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input data-testid="filter-verified" type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
              Verified members only
            </label>
          </div>

          {/* Nearby list */}
          {nearby.length > 0 && (
            <div className="pe-card rounded-2xl p-4">
              <div className="text-sm text-white font-medium mb-2">Closest to you</div>
              <div className="space-y-2">
                {nearby.map((p) => (
                  <button key={p.id} data-testid={`nearby-${p.id}`} onClick={() => navigate(`/creator/${p.id}`)} className="w-full flex items-center gap-3 text-left hover:bg-[#1A1A1A] rounded-lg p-1.5 transition-colors">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-[#1A1A1A] border border-[#2A2A2A] flex-shrink-0">{p.photo && <img src={p.photo} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-1">{p.name} {p.verified && <BadgeCheck size={12} className="text-[#D4AF37]" />}</div>
                      <div className="text-[11px] text-[#A0A0A0]">~{p.distance_km} km away</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
